import * as PIXI from 'pixi.js';
import { ViewportState, GridConfig } from '../types';
import { GRID_CONFIG } from '../utils/constants';
import { logger } from '../utils/logger';

export class GridRenderer {
  private grid: PIXI.Graphics;
  private config: GridConfig;

  constructor(grid: PIXI.Graphics, config: GridConfig = GRID_CONFIG) {
    this.grid = grid;
    this.config = config;
  }

  update(app: PIXI.Application, viewportState: ViewportState): void {
    if (!app || !this.grid || !app.renderer) {
      logger.pixi.warn('GridRenderer.update: Missing required components', {
        app: !!app,
        grid: !!this.grid,
        renderer: !!(app && app.renderer)
      });
      return;
    }

    try {
      this.grid.clear();
      
      const canvasWidth = app.renderer.width;
      const canvasHeight = app.renderer.height;
      const scale = viewportState.scale;
      const offsetX = viewportState.x;
      const offsetY = viewportState.y;
      
      // Determine grid parameters based on zoom level
      let gridSize = this.config.minorGridSize;
      let color = this.config.minorGridColor;
      let alpha = this.config.minorGridAlpha;
      
      if (scale < 0.5) {
        gridSize = this.config.majorGridSize;
        color = this.config.majorGridColor;
        alpha = this.config.majorGridAlpha;
      }
      
      // Calculate visible world bounds
      const worldLeft = -offsetX / scale;
      const worldTop = -offsetY / scale;
      const worldRight = (canvasWidth - offsetX) / scale;
      const worldBottom = (canvasHeight - offsetY) / scale;
      
      // Calculate grid line positions
      const startX = Math.floor(worldLeft / gridSize) * gridSize;
      const endX = Math.ceil(worldRight / gridSize) * gridSize;
      const startY = Math.floor(worldTop / gridSize) * gridSize;
      const endY = Math.ceil(worldBottom / gridSize) * gridSize;
      
      // Draw vertical lines
      for (let x = startX; x <= endX; x += gridSize) {
        const screenX = x * scale + offsetX;
        this.grid.moveTo(screenX, 0);
        this.grid.lineTo(screenX, canvasHeight);
      }
      
      // Draw horizontal lines
      for (let y = startY; y <= endY; y += gridSize) {
        const screenY = y * scale + offsetY;
        this.grid.moveTo(0, screenY);
        this.grid.lineTo(canvasWidth, screenY);
      }
      
      this.grid.stroke({ width: 1, color: color, alpha: alpha });
    } catch (error) {
      logger.pixi.warn('Grid update failed', error);
    }
  }
}